import { computed, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  Event as RouterEvent,
} from '@angular/router';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { AuthService } from '../services/auth/auth.service';
import { PermissionService } from '../services/permission.service';
import { Session } from '../models/data.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';
import { MediaPermissionsErrorType } from 'mic-check';
import { ResourceType, ActionType } from '../types/permission.types';

type AppState = {
  loading: boolean;
  ready: boolean;
  darkMode: boolean;
  session: Session;
  mediaPermissionsError: MediaPermissionsErrorType | null;
  campaign: any; // Full campaign data (settings, prizes, etc.)
  // permissionMessage: string;
};

const initialState: AppState = {
  loading: false,
  ready: false,
  darkMode: false,
  session: {
    id: ''
  },
  mediaPermissionsError: MediaPermissionsErrorType.Generic,
  campaign: null,
  // permissionMessage: 'Checking camera permissions...'
};

export const AppStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store, authService = inject(AuthService), permissionService = inject(PermissionService)) => ({
    user: computed(() => authService.user()),
    userRole: computed(() => permissionService.userRole()),
    userClaims: computed(() => permissionService.userClaims()),
    isAuthenticated: computed(() => !!authService.user()),
    canAccessDashboard: computed(() => permissionService.hasPermission('campaigns', 'read')),
    canManageUsers: computed(() => permissionService.hasPermission('users', 'manage')),
    canManageCampaigns: computed(() => permissionService.hasPermission('campaigns', 'manage')),
    canViewAnalytics: computed(() => permissionService.hasPermission('analytics', 'read')),
  })),
  withMethods((
    store,
    router = inject(Router),
    authService = inject(AuthService),
    snackbar = inject(MatSnackBar)
  ) => ({
    startLoading: () => {
      patchState(store, { loading: true });
    },
    stopLoading: () => {
      patchState(store, { loading: false });
    },
    setMediaPermissionsError: (error: MediaPermissionsErrorType | null) => {
      patchState(store, { mediaPermissionsError: error });
    },
    // setPermissionsMessage: (message: string) => {
    //   patchState(store, { permissionMessage: message });
    // },
    setSession: (session: Session) => {
      patchState(store, { session: {...session} });
    },
    setCampaign: (campaign: any) => {
      patchState(store, { campaign });
    },
    setReady: () => {
      patchState(store, { ready: true });
    },
    setDarkMode: (darkMode: boolean) => {
      patchState(store, { darkMode });
      document.documentElement.classList.toggle('dark', darkMode);
    },
    login: async (email: string, password: string) => {
      if (!email || !password) {
        return;
      }

      try {
        patchState(store, { loading: true });

        await authService.login(email, password);

        router.navigate(['/dashboard']);
      } catch (error) {
        console.error('Login failed in AppStore:', error);
        snackbar.open('Invalid email or password', 'Close', {
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      } finally {
        patchState(store, { loading: false });
      }
    },
    logout: async () => {
      await authService.logout();
      router.navigate(['/login']);
    },
    resetPassword: async (email: string) => {
      if (!email) {
        return;
      }

      try {
        patchState(store, { loading: true });
        await authService.resetPassword(email);
        snackbar.open('Password reset email sent', 'Close', {
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      } catch (error) {
        snackbar.open('Invalid email', 'Close', {
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      } finally {
        patchState(store, { loading: false });
      }
    },
    confirmPasswordReset: async (code: string, newPassword: string) => {
      if (!code || !newPassword) {
        return;
      }

      try {
        patchState(store, { loading: true });
        await authService.confirmPasswordReset(code, newPassword);
        snackbar.open('Password reset successful', 'Close', {
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      } catch (error) {
        snackbar.open('Invalid code or password', 'Close', {
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      } finally {
        patchState(store, { loading: false });
      }
    },
    // Permission-related methods
    hasPermission: (resource: ResourceType, action: ActionType, scope?: { clientId?: string, propertyId?: string, campaignId?: string }) => {
      const permissionService = inject(PermissionService);
      return permissionService.hasPermission(resource, action, scope);
    },
    canAccessClient: (clientId: string) => {
      const permissionService = inject(PermissionService);
      return permissionService.canAccessClient(clientId);
    },
    canAccessProperty: (propertyId: string) => {
      const permissionService = inject(PermissionService);
      return permissionService.canAccessProperty(propertyId);
    },
    canAccessCampaign: (campaignId: string) => {
      const permissionService = inject(PermissionService);
      return permissionService.canAccessCampaign(campaignId);
    },
    loaderOnNavigation: rxMethod<RouterEvent>(
      pipe(
        tap((event) => {
          if (event instanceof NavigationStart) {
            patchState(store, { loading: true });
          } else if (
            event instanceof NavigationEnd ||
            event instanceof NavigationError ||
            event instanceof NavigationCancel
          ) {
            patchState(store, { loading: false });
          }
        })
      )
    ),
  })),
  withHooks((store, router = inject(Router)) => ({
    onInit() {
      store.loaderOnNavigation(router.events);
    },
  }))
);
